import React, { useMemo } from 'react';
import { SectionFolder } from '@src/components/SectionFolder';
import { SectionTitle } from '@src/components/SectionTitle';
import { SectionProps } from '../types/Settings';
import { LocalizedStrings } from '@src/utils/localizedStrings';
import { 
  SECTION_TITLE_SIZE, 
  FORM_LINE_TITLE_SIZE, 
  MIN_SIZE_VALUE, 
  CONTENT_SIZE_DEFAULT
} from '../utils/variables';
import { SectionContent } from '../subcomponents/SectionContent';
import { DesignIcon } from '../../Icons/DropDownIcons/Design';
import { QuickLayoutSelector } from '../selectors/QuickLayoutSelector';
import { FormLine } from '@src/components/Form/FormLine';
import { ContentSizeSlider } from '../selectors/ContentSizeSlider';
import { style } from 'typestyle';
import { useQuickDesign } from '../hooks/useQuickDesign';
import { ThumbnailShapeSelector } from '../selectors/ThumbnailShapeSelector';
import { ThumbnailCornersSelector } from '../selectors/ThumbnailCornersSelector';
import { FineTunedOverlay } from '../subcomponents/FineTunedOverlay';
import { CarouselTypeSelector } from '../selectors/CarouselTypeSelector';
import { resolveCarouselTypeSelection } from '../utils/carouselType';

interface QuickDesignProps extends SectionProps {
  showThumbnails?: boolean;
  onGoToAdvancedSettings?: () => void;
}

interface LayoutChangeCallback {
  onLayoutChange?: (layout: string) => void;
}

const QuickDesign = ({ 
  opened, 
  onOpen, 
  editedWidget, 
  setEditedWidget, 
  showThumbnails = true,
  onLayoutChange,
  onGoToAdvancedSettings
}: QuickDesignProps & LayoutChangeCallback) => {
  const { 
    currentLayout, 
    applyLayout, 
    updateContentSize, 
    supportsContentSize,
    updateThumbnailShape,
    updateThumbnailCorners,
    updateCarouselType
  } = useQuickDesign({ 
    editedWidget, 
    setEditedWidget 
  });

  const selectedCarouselType = resolveCarouselTypeSelection(
    editedWidget.settings.carousel_type,
    editedWidget.settings.shop_this_look_display,
  );

  // Check if content size is beyond the slider's max value (fine-tuned)
  const isContentSizeFineTuned = useMemo(() => {
    return editedWidget.settings.content_size > 10;
  }, [editedWidget.settings.content_size]);

  // Reset content size to default (5)
  const resetContentSize = () => {
    updateContentSize(5);
  };
  
  return (
    <SectionFolder
      title={
        <SectionTitle size={SECTION_TITLE_SIZE}>
          <DesignIcon />
          {lang.title.main}
        </SectionTitle>
      }
      opened={opened}
      onOpen={onOpen}
    >
      <SectionContent>
        <QuickLayoutSelector
          layout={currentLayout}
          onChange={(layout) => {
            applyLayout(layout);
            if (onLayoutChange) {
              onLayoutChange(layout);
            }
          }}
        />

        {currentLayout === 'carousel' ? (
          <FormLine title={lang.title.carouselType} size={FORM_LINE_TITLE_SIZE}>
            <CarouselTypeSelector
              value={selectedCarouselType}
              onChange={(type) => updateCarouselType(type)}
            />
          </FormLine>
        ) : null}

        {supportsContentSize() ? (
          <FormLine title={lang.title.contentsPerRow} size={FORM_LINE_TITLE_SIZE}>
            <div className={contentSizeContainer}>
              <div className={sliderContainer}>
                {isContentSizeFineTuned ? (
                  <FineTunedOverlay
                    onReset={resetContentSize}
                    onAdvancedSettings={onGoToAdvancedSettings || (() => {})}
                  />
                ) : (
                  <ContentSizeSlider
                    value={editedWidget.settings.content_size || CONTENT_SIZE_DEFAULT}
                    onChange={(value) => updateContentSize(value)}
                    min={MIN_SIZE_VALUE}
                    max={10}
                  />
                )}
              </div>
            </div>
          </FormLine>
        ) : null }

        <FormLine title={lang.title.shape} size={FORM_LINE_TITLE_SIZE}>
          <ThumbnailShapeSelector
            thumbnail_shape={editedWidget.settings.thumbnail_shape}
            onChange={(shape) => updateThumbnailShape(shape)}
          />
        </FormLine>

        <FormLine title={lang.title.corners} size={FORM_LINE_TITLE_SIZE}>
          <ThumbnailCornersSelector
            thumbnail_corners={editedWidget.settings.thumbnail_corners}
            onChange={(corners) => updateThumbnailCorners(corners)}
          />
        </FormLine>
      </SectionContent>
    </SectionFolder>
  );
};

const contentSizeContainer = style({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  width: '100%'
});

const sliderContainer = style({
  width: '90%',
  marginLeft: '10px'
});



const lang = LocalizedStrings({
  en: {
    title: {
      main: 'Design',
      carouselType: 'Carousel Type',
      contentsPerRow: 'Content per row',
      shape: 'Thumbnail Shape',
      corners: 'Thumbnail Corners'
    },
  
  },
});

export default QuickDesign;
